import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Plus } from 'lucide-react';
import { GlassPage } from '../components/chrome';

// --- Components ---

const FadeInSection = ({ children }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(domRef.current);
        }
      });
    }, { threshold: 0.1 });

    if (domRef.current) {
      observer.observe(domRef.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={domRef}
      className={`transition-all duration-1000 ease-out transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
    >
      {children}
    </div>
  );
};

const ScrollRevealText = ({ content, className }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!elementRef.current) return;
      const { top, height } = elementRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const startReveal = windowHeight * .8;
      const endReveal = windowHeight * .6;

      const distance = startReveal - top;
      const totalRange = startReveal - endReveal + height * 0.5;

      const progress = Math.max(0, Math.min(1, distance / totalRange));
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const paragraphs = content.split('\n').filter(p => p.trim() !== '');
  const allText = paragraphs.join(' ');
  const words = allText.split(' ').filter(w => w.length > 0);
  const totalWordCount = words.length;
  const activeWordIndex = Math.ceil(scrollProgress * totalWordCount) - 1;

  let globalWordIndex = 0;

  return (
    <div ref={elementRef} className={className}>
      {paragraphs.map((paragraph, pIdx) => {
        const paragraphWords = paragraph.split(' ');
        return (
          <p key={pIdx} className="mb-8 last:mb-0">
            {paragraphWords.map((word, wIdx) => {
              const isWordActive = globalWordIndex <= activeWordIndex;
              globalWordIndex++;
              return (
                <span key={wIdx}>
                  {word.split('').map((letter, lIdx) => (
                    <span
                      key={lIdx}
                      className={`transition-colors duration-200 ${isWordActive ? 'text-zinc-900' : 'text-zinc-300'}`}
                    >
                      {letter}
                    </span>
                  ))}
                  {' '}
                </span>
              );
            })}
          </p>
        );
      })}
    </div>
  );
}

const ModelBackground = ({ modelPath = '/models/model.glb' }) => {
  const wrapperRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    let renderer, scene, fallbackCamera, mixer, model, canvas, rafId;
    let activeCamera = null;
    let onResize;

    const loadScript = (src) => new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = res;
      s.onerror = rej;
      document.head.appendChild(s);
    });

    const cleanup = () => {
      mounted = false;
      if (onResize) window.removeEventListener('resize', onResize);
      if (rafId) cancelAnimationFrame(rafId);
      if (scene && model) {
        scene.remove(model);
        if (model.traverse) {
          model.traverse(node => {
            if (node.geometry) node.geometry.dispose();
            if (node.material) {
              if (Array.isArray(node.material)) {
                node.material.forEach(mat => mat.dispose());
              } else {
                node.material.dispose();
              }
            }
          });
        }
      }
      if (renderer) {
        renderer.dispose();
        renderer.forceContextLoss();
      }
      if (canvas && canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    };

    cleanupRef.current = cleanup;

    (async () => {
      try {
        if (!window.THREE) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js');
        if (!window.THREE.GLTFLoader) await loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js');

        if (!mounted) return;
        const THREE = window.THREE;
        const GLTFLoader = THREE.GLTFLoader;

        const wrapper = wrapperRef.current;
        if (!wrapper || !mounted) return;

        canvas = document.createElement('canvas');
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.display = 'block';
        wrapper.appendChild(canvas);

        renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
        const isMobileInit = window.innerWidth < 768;
        renderer.setPixelRatio(isMobileInit ? 1.0 : window.devicePixelRatio || 1);
        renderer.setSize(wrapper.clientWidth, wrapper.clientHeight, false);
        renderer.setClearColor(0x000000, 0);
        renderer.outputEncoding = THREE.sRGBEncoding;

        scene = new THREE.Scene();

        fallbackCamera = new THREE.PerspectiveCamera(35, wrapper.clientWidth / wrapper.clientHeight, 0.1, 1000);
        fallbackCamera.position.set(0, 0, 5);
        activeCamera = fallbackCamera;

        const ambient = new THREE.AmbientLight(0xffffff, 0.02);
        scene.add(ambient);
        const dir1 = new THREE.DirectionalLight(0xffffff, 0.03);
        dir1.position.set(-200, 200, 200);
        scene.add(dir1);

        const loader = new GLTFLoader();
        loader.load(
          modelPath,
          (gltf) => {
            if (!mounted) return;
            model = gltf.scene;
            scene.add(model);

            console.log('[Korsa 3D] Loaded model:', {
              animationCount: gltf.animations.length,
              animations: gltf.animations.map(clip => ({
                name: clip.name,
                duration: clip.duration.toFixed(2) + 's',
                tracks: clip.tracks.map(t => t.name)
              })),
              cameraCount: gltf.cameras.length,
              cameras: gltf.cameras.map(c => c.name || c.type)
            });

            if (gltf.cameras && gltf.cameras.length > 0) {
              activeCamera = gltf.cameras[0];
              activeCamera.aspect = wrapper.clientWidth / wrapper.clientHeight;
              activeCamera.updateProjectionMatrix();
            }

            if (gltf.animations && gltf.animations.length > 0) {
              mixer = new THREE.AnimationMixer(model);
              let maxDuration = 0;

              gltf.animations.forEach(clip => {
                const action = mixer.clipAction(clip);
                action.loop = THREE.LoopOnce;
                action.clampWhenFinished = true;
                action.play();
                maxDuration = Math.max(maxDuration, clip.duration);
              });

              mixer.__duration = maxDuration;
            }

            if (renderer && scene && activeCamera) {
              renderer.render(scene, activeCamera);
            }
          },
          undefined,
          (err) => console.error('Error loading model:', err)
        );

        onResize = () => {
          if (!mounted || !wrapper || !renderer || !activeCamera) return;
          renderer.setSize(wrapper.clientWidth, wrapper.clientHeight, false);
          activeCamera.aspect = wrapper.clientWidth / wrapper.clientHeight;
          activeCamera.updateProjectionMatrix();
        };

        const clock = new THREE.Clock();
        const renderLoop = () => {
          if (!mounted) return;

          // Play the model's intro animation once on load, then hold the final frame
          if (mixer) mixer.update(clock.getDelta());

          if (mounted && renderer && scene && activeCamera) {
            renderer.render(scene, activeCamera);
            rafId = requestAnimationFrame(renderLoop);
          }
        };

        window.addEventListener('resize', onResize);
        renderLoop();
      } catch (err) {
        console.error('Three.js Init Error:', err);
      }
    })();

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [modelPath]);

  return <div ref={wrapperRef} className="absolute top-[35%] left-[5%] w-[90%] h-[60%] md:top-[16%] md:left-0 md:w-full md:h-full z-0 pointer-events-none" />;
};

// External coverage about Korsa. Swap in real article links as they land.
// Each entry: source (publication), date, title (headline), blurb, url.
const NEWS_ARTICLES = [
  {
    source: 'YouTube',
    date: 'Nov 2025',
    title: 'Korsa demos its 2025 prototype',
    blurb: 'The first working prototype on camera, with live word retrieval and teleprompting running on real hardware.',
    url: 'https://www.youtube.com/@Korsahq'
  },
  {
    source: 'X / Twitter',
    date: '2026',
    title: 'Follow the build in public',
    blurb: 'Product updates, milestones, and announcements straight from the team.',
    url: 'https://x.com/korsahq'
  },
  {
    source: 'TikTok',
    date: '2026',
    title: 'Korsa behind the scenes',
    blurb: 'Short-form looks at the glasses, the lab, and the people building them.',
    url: 'https://www.tiktok.com/@korsahq'
  }
];

const App = () => {
  return (
    <GlassPage>
      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        <div
          className="absolute inset-0 z-1 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at center, rgba(255, 255, 255, 0) 30%, rgba(238, 241, 246, 0.85) 100%)',
          }}
        />

        <ModelBackground modelPath="/models/model.glb" />

        {/* Headline at the top, clear of the glasses */}
        <div className="relative z-10 flex flex-col items-center text-center px-6 md:px-12 pt-28 md:pt-32">
          <div className="mb-4 animate-pulse text-zinc-400">
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold">Cognitive Technology</span>
          </div>

          <h1 className="text-[9vw] md:text-[4.5vw] font-bold leading-[0.95] tracking-tighter mb-4 uppercase text-zinc-900">
            Think Clearly.<br/>
            <span className="text-zinc-400">Speak Confidently</span>
          </h1>

          <p className="text-base md:text-sm text-zinc-500 font-medium leading-tight max-w-sm md:max-w-md">
            The world's first smart glasses designed with real-time cognitive assistance.
          </p>

          {/* CTAs above the glasses */}
          <div className="flex flex-col sm:flex-row items-center gap-4 mt-4 md:mt-5">
            <Link to="/purchase" className="btn-dark w-44">
              Order
            </Link>
            <a
              href="#korsaos"
              className="glass-pill w-44 inline-flex items-center justify-center px-8 py-4 text-[11px] font-bold uppercase tracking-widest text-zinc-900 hover:bg-white/85 transition-colors"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="relative py-16 md:py-24 px-4 md:px-6">
        <FadeInSection>
          <div className="glass max-w-6xl mx-auto px-8 md:px-16 py-16 md:py-24">
            <div className="grid md:grid-cols-12 gap-12">
              <div className="md:col-span-10">
                <h2 className="text-5xl md:text-[7vw] font-bold tracking-tighter leading-[0.9] uppercase mb-16 text-zinc-900">
                  Our Mission.
                </h2>
                <div className="max-w-3xl space-y-8 text-xl md:text-2xl font-medium leading-snug">
                  <ScrollRevealText
                    className=""
                    content={`Korsa is building the next generation of smart glasses using cognitive assistive technology to enhance how people communicate in everyday life.\nWe created smart glasses that support you as you talk. They look like ordinary glasses but to you, they are an essential piece of your day that makes communication easier.`}
                  />
                </div>
              </div>
            </div>
          </div>
        </FadeInSection>
      </section>

      {/* KorsaOS */}
      <section id="korsaos" className="py-16 md:py-24 px-4 md:px-6">
        <FadeInSection>
          <div className="glass max-w-6xl mx-auto px-8 md:px-16 py-16 md:py-20">
            <div className="flex flex-col md:flex-row justify-between md:items-end gap-4 mb-8 md:mb-16">
              <div>
                <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-bold mb-3">The software inside</span>
                <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-zinc-900">Korsa<span className="text-zinc-400">OS</span></h2>
              </div>
              <p className="text-sm text-zinc-500 max-w-sm md:text-right">The operating system for cognitive assistance. It understands the moment you're in and helps only when you need it. Always on, always private.</p>
            </div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2 px-4">A few examples</p>
            <div className="divide-y divide-zinc-900/5">
              {[
                { id: "01", title: "Word Retrieval", desc: "Predictive assistance for cognitive word-finding support." },
                { id: "02", title: "Factual Assist", desc: "Ask a question out loud, or get asked one, and the answer appears instantly on the lenses." },
                { id: "03", title: "Vocab Assist", desc: "When someone says a hard word in conversation, its definition surfaces instantly on the display." },
                { id: "04", title: "Live Translation", desc: "Real-time subtitle rendering for multi-lingual environments." },
                { id: "05", title: "Assistant", desc: "Real-time feedback and communication guidance." }
              ].map((f) => (
                <div key={f.id} className="group py-6 md:py-10 flex flex-col md:flex-row md:items-center justify-between md:hover:bg-white/40 rounded-2xl transition-colors md:cursor-crosshair px-4">
                  <div className="flex items-center space-x-6 md:space-x-12">
                    <span className="text-xs font-bold text-zinc-400">{f.id}</span>
                    <h3 className="text-2xl md:text-5xl font-bold tracking-tighter uppercase text-zinc-900">{f.title}</h3>
                  </div>
                  <div className="max-w-xs mt-2 md:mt-0">
                    <p className="text-xs md:text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
                  </div>
                  <Plus className="hidden md:block opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400" />
                </div>
              ))}
            </div>
            <div className="mt-8 md:mt-12 pt-6 md:pt-8">
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-400">+ And much more. KorsaOS grows with every over-the-air update</p>
            </div>
          </div>
        </FadeInSection>
      </section>

      {/* Newsroom */}
      <section id="news" className="py-16 md:py-24 px-4 md:px-6">
        <FadeInSection>
          <div className="glass max-w-6xl mx-auto px-8 md:px-16 py-16 md:py-20">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 md:mb-16">
              <h2 className="text-4xl md:text-6xl font-bold tracking-tighter uppercase text-zinc-900">Newsroom</h2>
              <p className="text-sm text-zinc-500 max-w-xs md:text-right">Korsa in the news: press, coverage, and mentions.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {NEWS_ARTICLES.map((article) => (
                <a
                  key={article.title}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col rounded-2xl bg-white/40 border border-white/70 p-6 md:p-8 hover:bg-white/70 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(31,38,135,0.12)] transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-900">{article.source}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{article.date}</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-900 mb-3">{article.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed flex-1">{article.blurb}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-900">
                    Read
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              ))}
            </div>

            <div className="mt-12 flex justify-center">
              <Link
                to="/contact"
                state={{ subject: 'Press Inquiry' }}
                className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-900 transition-colors"
              >
                Press inquiries → Contact us
              </Link>
            </div>
          </div>
        </FadeInSection>
      </section>

      {/* Careers Preview */}
      <section className="py-16 md:py-24 px-4 md:px-6">
        <FadeInSection>
          <div className="glass max-w-6xl mx-auto px-8 md:px-16 py-16 md:py-20">
            <div className="mb-12 md:mb-16">
              <h2 className="text-5xl md:text-7xl font-bold tracking-tighter uppercase leading-[0.9] text-zinc-900">JOIN OUR TEAM</h2>
            </div>
            <div className="border-t border-zinc-900/5">
              {[
                { title: "Machine Learning Engineer", dept: "Software & AI", desc: "Model development and optimization" },
                { title: "Data Engineer", dept: "Software & AI", desc: "Data pipeline and processing systems" },
                { title: "Optical Engineer", dept: "Hardware", desc: "Lens systems and projection technology" },
                { title: "Electrical Engineer", dept: "Hardware", desc: "Circuit design and power management" },
                { title: "Audio Engineer", dept: "Hardware", desc: "Sound processing and optimization" },
              ].map((job, i) => (
                <Link key={i} to="/careers" className="group border-b border-zinc-900/5 py-5 md:py-6 flex items-center justify-between hover:bg-white/40 rounded-xl transition-all duration-300 px-2 md:px-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-8">
                    <h3 className="text-lg md:text-xl font-bold tracking-tight text-zinc-900">{job.title}</h3>
                    <span className="text-xs text-zinc-500">{job.desc}</span>
                  </div>
                  <ArrowRight size={16} className="text-zinc-400 group-hover:text-zinc-900 group-hover:translate-x-1 transition-all shrink-0" />
                </Link>
              ))}
            </div>
            <div className="mt-12 flex justify-center">
              <Link to="/careers" className="btn-dark">
                View All Positions <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </FadeInSection>
      </section>
    </GlassPage>
  );
};

export default App;
